import type { TextStyle, ViewStyle } from 'react-native';

/**
 * Composer sizing constants.
 *
 * The composer presents ~4 visible lines at rest (100px at 24px line-height),
 * grows line-by-line up to MAX_*, and shrinks back on delete. Native and web
 * have separate min/max because gifted-chat's auto-sizing behaves differently
 * across platforms (see WebComposer for the web auto-size workaround).
 *
 * The IOS_*_KEYBOARD_OPEN constant constrains the composer when the soft
 * keyboard is up so it doesn't push the composer off-screen.
 */
export const MAX_COMPOSER_HEIGHT = 300;

export const MIN_COMPOSER_HEIGHT = 36;

export const MIN_NATIVE_COMPOSER_HEIGHT = 36;

export const MAX_NATIVE_COMPOSER_HEIGHT = 220;

export const MAX_NATIVE_COMPOSER_HEIGHT_SMALL_DEVICE = 180;

export const MAX_IOS_COMPOSER_HEIGHT_WHEN_KEYBOARD_OPEN = 96;

/**
 * The native composer's maximum as a share of the window, so a long reply,
 * such as a handed-off reply draft, can be read in place before it is sent
 * rather than scrolled four lines at a time. The fixed maximums above stay as
 * floors for a short window. With the keyboard up the share is smaller, so the
 * message the expert is answering stays in view above it.
 */
export const NATIVE_COMPOSER_WINDOW_SHARE = 0.45;

export const IOS_COMPOSER_WINDOW_SHARE_WHEN_KEYBOARD_OPEN = 0.28;

/** The native composer's maximum height for a window height and keyboard state. */
export function nativeComposerMaxHeight({
  windowHeight,
  isKeyboardVisible,
  isCompactLayout,
  isIOS,
}: {
  windowHeight: number;
  isKeyboardVisible: boolean;
  isCompactLayout: boolean;
  isIOS: boolean;
}): number {
  if (isIOS && isKeyboardVisible) {
    return Math.max(
      MAX_IOS_COMPOSER_HEIGHT_WHEN_KEYBOARD_OPEN,
      Math.round(windowHeight * IOS_COMPOSER_WINDOW_SHARE_WHEN_KEYBOARD_OPEN)
    );
  }

  const floor = isCompactLayout
    ? MAX_NATIVE_COMPOSER_HEIGHT_SMALL_DEVICE
    : MAX_NATIVE_COMPOSER_HEIGHT;

  return Math.max(floor, Math.round(windowHeight * NATIVE_COMPOSER_WINDOW_SHARE));
}

export const MIN_IOS_COMPOSER_HEIGHT = 36;

export const NATIVE_COMPOSER_KEYBOARD_GAP = 6;

/**
 * Corner radius for the native composer. The composer is two stacked
 * containers — the input (top, `primaryStyle`) and the toolbar accessory
 * (bottom, `accessoryStyle`) — both styled inline in ChatContent. They share
 * this one radius so all four corners stay aligned; PR #1046 once regressed
 * this by rounding only the bottom to 24px. Both containers spread the corner
 * objects below, so the radii can't drift (see composerHeights.test.ts).
 */
export const COMPOSER_CORNER_RADIUS = 8;

/** Top corners of the composer's primary (input) container. */
export const composerPrimaryCornerStyle = {
  borderTopLeftRadius: COMPOSER_CORNER_RADIUS,
  borderTopRightRadius: COMPOSER_CORNER_RADIUS,
} satisfies ViewStyle;

/** Bottom corners of the composer's accessory (toolbar) container. */
export const composerAccessoryCornerStyle = {
  borderBottomLeftRadius: COMPOSER_CORNER_RADIUS,
  borderBottomRightRadius: COMPOSER_CORNER_RADIUS,
} satisfies ViewStyle;

export const BASE_INPUT_STYLES: TextStyle = {
  fontFamily: 'Inter-Regular',
  fontWeight: '400',
  fontSize: 16,
  lineHeight: 24,
  flex: 1,
  flexBasis: 0,
  minWidth: 0,
  marginLeft: 0,
  textAlign: 'left',
};
